import type { FlightProcessingBucket, FlightProcessingCounts } from "@ca/types/flights";

export type FlightProgressLegendEntry = {
	class: string;
	key: string;
	label: string;
};

export const FLIGHT_SEGMENTS = [
	{
		class: "bg-success-500", key: "done", label: "Complete",
	},
	{
		class: "bg-primary-500", key: "running", label: "Scoring risk",
	},
	{
		class: "bg-primary-500/40", key: "queued", label: "Risk queued",
	},
	{
		class: "bg-info-500", key: "analyzing", label: "Analyzing flight data",
	},
	{
		class: "bg-info-500/40", key: "fetching", label: "Fetching flight data",
	},
	{
		class: "bg-elevated", key: "waiting", label: "Waiting",
	},
	{
		class: "bg-orange-500", key: "retrying", label: "Retrying",
	},
	{
		class: "bg-stone-300", key: "inProgress", label: "Not landed yet",
	},
	{
		class: "bg-stone-400", key: "noData", label: "No flight data",
	},
	{
		class: "bg-error-500", key: "failed", label: "Risk failed",
	},
] as const satisfies readonly (FlightProgressLegendEntry & {
	key: keyof FlightProcessingCounts;
})[];

export const PULLING_SEGMENT: FlightProgressLegendEntry = {
	class: "animate-pulse bg-accented",
	key: "pulling",
	label: "Pulling more flights",
};

export const flightSegments = (flights: FlightProcessingCounts) =>
	FLIGHT_SEGMENTS.map(segment => ({
		class: segment.class,
		count: flights[segment.key],
		key: segment.key as string,
	}));

export const flightTotal = (flights: FlightProcessingCounts) =>
	FLIGHT_SEGMENTS.reduce((sum, segment) => sum + flights[segment.key], 0);

export const flightLegend = (
	rows: FlightProcessingCounts[],
	provisional: boolean,
): FlightProgressLegendEntry[] => {
	const present: FlightProgressLegendEntry[] = FLIGHT_SEGMENTS
		.filter(segment => rows.some(flights => flights[segment.key] > 0))
		.map(segment => ({ ...segment }));
	if (provisional) {
		present.push({ ...PULLING_SEGMENT });
	}
	return present;
};

const TERMINAL_BUCKETS = new Set(["done", "failed", "inProgress"]);

export const isFlightProcessing = (bucket: FlightProcessingBucket | undefined) =>
	bucket != null && !TERMINAL_BUCKETS.has(bucket);

export const FLIGHT_SEGMENT_CLASS = Object.fromEntries(
	FLIGHT_SEGMENTS.map(segment => [segment.key, segment.class]),
) as Record<keyof FlightProcessingCounts, string>;
